/**
 * usePromotionCandidates Hook
 *
 * V62 SSoT: BE getPromotionCandidates callable 기반으로 통일.
 * FE 자체 계산 로직 완전 제거 — BE promotionEngine만 사용.
 *
 * **기능:**
 * - BE에서 진급 후보자 산출 (promotionEngine.evaluatePromotion)
 * - 클라이언트 사이드 필터링 (orgUnit, positionId, searchText, eligibilityFilter)
 * - 진급 가능자 우선 정렬
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  PromotionCandidate,
  PromotionCandidateFilter,
} from '../types/promotionCandidate';

/**
 * V99: 데이터 소스 진단 정보
 */
export interface DataSourceDiagnostics {
  employees: { count: number; isLoading: boolean; error: string | null };
  positions: { count: number; isLoading: boolean; error: string | null };
  policy: { minPositionYears: number | null; isLoading: boolean; error: string | null };
  regularStartDateMap: { count: number; isLoading: boolean; error: string | null };
}

/**
 * usePromotionCandidates Hook 반환 타입
 */
export interface UsePromotionCandidatesResult {
  candidates: PromotionCandidate[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  diagnostics: DataSourceDiagnostics;
}

/**
 * BE getPromotionCandidates 응답의 candidate 타입
 */
interface BePromotionCandidate {
  userId: string;
  nameKo: string;
  employeeNumber: string;
  email?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  currentPositionId: string;
  currentPositionTitle: string;
  currentPositionOrder: number;
  nextPositionId: string | null;
  nextPositionTitle: string | null;
  ruleType: 'RECOGNIZED_YEARS';
  effectiveYears: number;
  requiredYearsForNextPosition: number | null;
  yearsDifference: number | null;
  positionYears: number;
  recognizedYearsFromHire: number;
  isEligible: boolean;
  reasonCodes: string[];
  reason: string;
  ngleFirstHireDate?: string;
  currentPositionStartDate?: string;
  nextPositionRequiredRecognizedYearsFromHire?: number | null;
  recognizedYearsBreakdown?: {
    educationYears: number;
    priorCareerYears: number;
    workYears: number;
    promotionBonusYears: number;
  };
  // V67 RCA: BE promotionEngine 의 users SSoT 원본 값 (모달 input prefill 용).
  //   missing(undefined) 과 0 을 구분하기 위해 원본 그대로 전달됨.
  employee?: {
    promotionEducationYears?: number;
    priorCareerYears?: number;
    promotionBonusYears?: number;
    educationYears?: number;
    promotionEducationLevel?: string;
    educationLevel?: string;
    educationLevelCode?: string;
  };
}

interface BeResponse {
  success: boolean;
  candidates: BePromotionCandidate[];
  totalCount: number;
  eligibleCount: number;
  referenceDate: string;
}

/**
 * BE candidate → FE PromotionCandidate 매핑
 */
function mapBeToFeCandidate(be: BePromotionCandidate): PromotionCandidate {
  return {
    // employee 객체 (BE flat → FE nested)
    // V67 RCA: BE promotionEngine.PromotionCandidate.employee 의 원본 SSoT 필드들을
    //   FE 모달이 prefill 에 사용하므로 누락 없이 전달.
    //   기존 매퍼는 id/nameKo 등만 만들고 educationYears/priorCareerYears/promotionEducationLevel 등을
    //   모두 버려서 모달이 항상 0 / '-' 로 표시됨.
    employee: {
      id: be.userId,
      nameKo: be.nameKo,
      employeeNumber: be.employeeNumber,
      email: be.email || '',
      orgUnitId: be.orgUnitId || '',
      orgUnitName: be.orgUnitName || '',
      positionId: be.currentPositionId,
      positionTitle: be.currentPositionTitle,
      // V67 RCA: BE employee SSoT 필드 propagation
      promotionEducationYears: be.employee?.promotionEducationYears,
      priorCareerYears: be.employee?.priorCareerYears,
      promotionBonusYears: be.employee?.promotionBonusYears,
      educationYears: be.employee?.educationYears,
      promotionEducationLevel: be.employee?.promotionEducationLevel,
      educationLevel: be.employee?.educationLevel,
      educationLevelCode: be.employee?.educationLevelCode,
    } as any,

    // 현재 직위
    currentPosition: {
      id: be.currentPositionId,
      name: be.currentPositionTitle,
      order: be.currentPositionOrder,
      requiredRecognizedYearsFromHire: be.requiredYearsForNextPosition,
    } as any,

    // 다음 직위
    nextPosition: be.nextPositionId
      ? {
          id: be.nextPositionId,
          name: be.nextPositionTitle || '',
          requiredRecognizedYearsFromHire: be.nextPositionRequiredRecognizedYearsFromHire ?? null,
        } as any
      : null,

    // 날짜
    regularStartDate: be.ngleFirstHireDate || null,
    displayPositionStartDate: be.currentPositionStartDate,
    isManualPositionStartDate: !!be.currentPositionStartDate,

    // 계산 결과 (BE SSoT)
    recognizedYears: be.recognizedYearsFromHire,
    recognizedYearsBreakdown: be.recognizedYearsBreakdown || {
      educationYears: 0,
      priorCareerYears: 0,
      workYears: 0,
      promotionBonusYears: 0,
    },
    positionYears: be.positionYears,

    // 판정 결과 (BE SSoT)
    meetsCriteria: be.isEligible,
    reason: be.reason,
    yearsDifference: be.yearsDifference,
    positionYearsDifference: null,
    ruleType: be.ruleType,
  };
}

/**
 * 클라이언트 사이드 필터링 (orgUnit, position, search, eligibility)
 */
function filterCandidates(
  candidates: PromotionCandidate[],
  filters: PromotionCandidateFilter
): PromotionCandidate[] {
  return candidates.filter(c => {
    // orgUnitIds 필터 (subtree)
    if (filters.orgUnitIds && filters.orgUnitIds.length > 0) {
      if (!c.employee.orgUnitId || !filters.orgUnitIds.includes(c.employee.orgUnitId)) {
        return false;
      }
    }
    // orgUnitId 단일 필터 (orgUnitIds가 없을 때만)
    else if (filters.orgUnitId) {
      if (c.employee.orgUnitId !== filters.orgUnitId) {
        return false;
      }
    }

    // positionId 필터
    if (filters.positionId && c.currentPosition.id !== filters.positionId) {
      return false;
    }

    // searchText 필터
    if (filters.searchText) {
      const text = filters.searchText.toLowerCase();
      const nameMatch = c.employee.nameKo?.toLowerCase().includes(text);
      const numMatch = c.employee.employeeNumber?.toLowerCase().includes(text);
      if (!nameMatch && !numMatch) {
        return false;
      }
    }

    // eligibilityFilter 필터
    if (filters.eligibilityFilter === 'eligible' && !c.meetsCriteria) {
      return false;
    }
    if (filters.eligibilityFilter === 'ineligible' && c.meetsCriteria) {
      return false;
    }

    return true;
  });
}

/**
 * 정렬: 진급 가능자 우선 → 직위 높은 순 → 인정연한 많은 순
 */
function sortCandidates(candidates: PromotionCandidate[]): PromotionCandidate[] {
  return [...candidates].sort((a, b) => {
    if (a.meetsCriteria !== b.meetsCriteria) {
      return a.meetsCriteria ? -1 : 1;
    }
    if (a.currentPosition.order !== b.currentPosition.order) {
      return (b.currentPosition.order || 0) - (a.currentPosition.order || 0);
    }
    return b.recognizedYears - a.recognizedYears;
  });
}

/**
 * V62 SSoT: BE 기반 진급 후보자 산출 Hook
 */
export function usePromotionCandidates(
  filters: PromotionCandidateFilter
): UsePromotionCandidatesResult {
  const { referenceDate, orgUnitId, orgUnitIds, positionId, searchText, eligibilityFilter } = filters;

  const [allCandidates, setAllCandidates] = useState<PromotionCandidate[]>([]);
  const [candidates, setCandidates] = useState<PromotionCandidate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  // BE callable 호출
  useEffect(() => {
    if (!functions) {
      setAllCandidates([]);
      setCandidates([]);
      setIsLoading(false);
      return;
    }

    const fetchCandidates = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const getPromotionCandidatesFn = httpsCallable<
          { referenceDate: string; eligibleOnly: boolean },
          BeResponse
        >(functions!, 'getPromotionCandidates');

        const result = await getPromotionCandidatesFn({
          referenceDate,
          eligibleOnly: false, // 전체 조회 후 FE에서 필터
        });

        if (result.data.success) {
          const mapped = result.data.candidates.map(mapBeToFeCandidate);
          setAllCandidates(mapped);
        } else {
          setAllCandidates([]);
        }
      } catch (err) {
        console.error('[usePromotionCandidates] BE 호출 실패:', err);
        setError(err as Error);
        setAllCandidates([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCandidates();
  }, [referenceDate, refetchTrigger]);

  // 클라이언트 사이드 필터링 (orgUnit, position, search, eligibility)
  useEffect(() => {
    const filtered = filterCandidates(allCandidates, {
      referenceDate,
      orgUnitId,
      orgUnitIds,
      positionId,
      searchText,
      eligibilityFilter,
    });
    const sorted = sortCandidates(filtered);
    setCandidates(sorted);
  }, [allCandidates, orgUnitId, orgUnitIds, positionId, searchText, eligibilityFilter, referenceDate]);

  // diagnostics (호환성 유지)
  const diagnostics: DataSourceDiagnostics = {
    employees: { count: allCandidates.length, isLoading, error: error?.message || null },
    positions: { count: 0, isLoading: false, error: null },
    policy: { minPositionYears: null, isLoading: false, error: null },
    regularStartDateMap: { count: 0, isLoading: false, error: null },
  };

  return {
    candidates,
    isLoading,
    error,
    refetch,
    diagnostics,
  };
}
